<template>
  <!-- a-config-provider只是控制默认组件语言的显示，如datePicker -->
  <a-config-provider :locale="uiCurrentLocale">
    <template #renderEmpty>
      <div style="text-align: center; padding-top: 20px">
        <p>{{ $t('com_msg_empty') }}</p>
      </div>
    </template>
    <div id="app">
      <!-- 网络异常 -->
      <Error
        v-if="networkError"
        :reload="true"
        :title="$t('com_error_network')"
        :description="$t('com_error_network_description')"
      >
        <Icon type="networkerror2" />
      </Error>

      <!-- 全局loading -->
      <Loading :show.sync="loading" v-else-if="loading" />
      <router-view v-else class="g-layout-wrapper" />
      <Freeze :show="freeze" />

      <!-- 隐私协议 -->
      <a-modal
        :title="$t('user_privacy_title')"
        :visible="privacyShow"
        destroyOnClose
        :maskClosable="false"
        :closable="false"
        width="80%"
        :mask="false"
      >
        <div class="privacy-content" v-html="privacyContent"></div>

        <template slot="footer">
          <div class="privacy-content-footer">
            <div class="privacy-content-footer-checkbox">
              <a-checkbox v-model="privacyChecked">
                {{ $t('user_privacy_check') }}
              </a-checkbox>
            </div>

            <div class="privacy-content-footer-btn">
              <a-button @click="cancelPrivacy" :loading="privacyCancelLoading" size="large">
                {{ $t('com_btn_logout') }}
              </a-button>
              <a-button
                type="primary"
                :loading="privacyConfirmLoading"
                @click="handlePrivacy"
                size="large"
                :disabled="!privacyChecked || privacyCancelLoading"
              >
                {{ $t('com_btn_confirm') }}
              </a-button>
            </div>
          </div>
        </template>
      </a-modal>
    </div>
  </a-config-provider>
</template>

<script>
import 'moment/locale/zh-cn';
import { setCookie } from '@/libs/cookie';
import { UserInfo } from '@/api/common';
import { mapGetters } from 'vuex';
import Freeze from '_c/Freeze';
import { isManage } from '@/libs/utils';
import Error from '@/components/Error';
// import MobileDetect from 'mobile-detect';
import DOMPurify from 'dompurify';
import { LogoutSubmit } from '@/api/login';
import { confirmPrivacyPolicy } from '@/api/system/privacy';

export default {
  components: {
    Freeze,
    Error,
  },
  data() {
    return {
      networkError: false,

      // 不需要判断登录的页面
      noLoginPages: ['/find-password', '/find-password/reset', '/share/file'],

      // 暂存userInfo
      userInfo: null,

      // 是否显示协议
      privacyShow: false,
      privacyInfo: null,
      privacyContent: '',
      privacyConfirmLoading: false,
      privacyCancelLoading: false,
      privacyChecked: false,
    };
  },
  created() {
    // const md = new MobileDetect(window.navigator.userAgent);
    // 如果是移动端，则跳转到移动端界面
    // if (md.mobile()) {
    //   console.log('Current Device Is Mobile', true);
    //   const url = window.location.href;
    //   if (/\/\/p\./gi.test(url)) {
    //     // p.xxx的移动端地址替换为m.xxx的移动端地址
    //     const newURL = url.replace('//p.', '//m.');
    //     window.location.href = newURL;
    //   }
    // } else {
    //   console.log('Current Device Is Mobile', false);
    // }

    document.title = this.$t('com_header_title');

    // 判断页面是否需要登录，需要登录则获取用户信息
    if (!this.noLoginPages.includes(window.location.pathname)) {
      // 获取用户信息
      this.getUserInfo();
    } else {
      // 不需判断是否登录，直接显示
      this.$store.commit('setLoading', false);
    }
  },
  computed: {
    ...mapGetters(['uiCurrentLocale', 'token', 'freeze', 'token', 'authList', 'isSuperAdmin']),

    loading: {
      get() {
        return this.$store.state.loading;
      },
      set(val) {
        this.$store.commit('setLoading', val);
      },
    },
  },
  methods: {
    // 协议
    handlePrivacy() {
      // 确认协议后，直接调用handlePrivacy
      this.privacyConfirmLoading = true;
      confirmPrivacyPolicy({
        version: this.privacyInfo.newPrivacyPolicyVersion,
      })
        .then(res => {
          if (res.code === 0) {
            this.privacyShow = false;
            this.handleUserInfo(this.userInfo);
          }
        })
        .finally(() => {
          this.privacyConfirmLoading = false;
        });
    },
    cancelPrivacy() {
      // 退出登录
      this.privacyCancelLoading = true;
      LogoutSubmit()
        .then(res => {
          if (res.code === 0) {
            // 清除现有登录态
            this.$store.commit('clearSession', null);
            window.location.href = '/login';
          }
        })
        .finally(() => {
          this.privacyCancelLoading = false;
        });
    },

    /**
     * 获取用户信息、token、csrf
     */
    getUserInfo() {
      UserInfo()
        .then(res => {
          if (res && res.data && res.code === 0) {
            this.userInfo = res;

            // 是否显示协议
            if (
              res.data &&
              res.data.privacy &&
              res.data.privacy.needConfirm &&
              res.data.privacy.content &&
              res.data.privacy.newPrivacyPolicyVersion
            ) {
              this.privacyInfo = res.data.privacy;
              // 使用 DOMPurify 清理 HTML
              this.privacyContent = DOMPurify.sanitize(res.data.privacy.content, {
                ALLOWED_ATTR: ['class', 'id', 'style', 'href', 'style'], // 指定允许保留的属性
              });
              this.privacyShow = true;
              return;
            }

            // 处理info
            this.handleUserInfo(this.userInfo);
          } else {
            // 关闭loading
            this.$store.commit('setLoading', false);
            // 否则跳转到登录页面
            this.gotoLogin();
          }
        })
        .catch(err => {
          // 关闭loading
          this.$store.commit('setLoading', false);
          // 初始接口报错时，跳转到登录页面
          this.gotoLogin();
          this.networkError = true;
          console.log(err);
        });
    },

    // 处理业务数据
    handleUserInfo(res) {
      // 设置csrf
      setCookie('x-csrf', res.data.csrfToken);

      if (res.data && res.data.user && res.data.user.userName) {
        // 是否超级管理员
        this.$store.commit('setIsSuperAdmin', !!res.data.user.root);

        // 是否为infypower的客户（用户）
        this.$store.commit('setIsInfypowerCustomer', !!res.data.user.infypower);

        // 当前用户
        this.$store.commit('setUserInfo', res.data);

        // 设置token
        this.$store.commit('setToken', res.data.tokenId);

        // 存放Aliyun OSS的配置
        // this.$store.commit('setOssConfig', res.data?.oss);

        // 关闭loading
        this.$store.commit('setLoading', false);
        // 登录成功的跳转
        this.getRedirectLink();
      } else {
        // 关闭loading
        this.$store.commit('setLoading', false);
        // 未登录跳转登录页面
        this.gotoLogin();
      }
    },

    getRedirectLink() {
      // 跳转
      const pathname = window.location.pathname;
      let redirctUrl = '';
      if (pathname == '/login') {
        if (this.authList.includes('auth_home')) {
          // 如果有权限，跳转到首页
          this.goRedirect('/manage/home');
        } else {
          // 否则跳转到欢迎页
          this.goRedirect('/manage/welcome');
        }
      } else {
        if (pathname === '' || pathname === '/') {
          // 根目录跳转
          if (this.authList.includes('auth_home') || this.isSuperAdmin) {
            this.goRedirect('/manage/home');
          } else {
            this.goRedirect('/manage/welcome');
          }
        } else {
          // 非根目录跳转
          redirctUrl = window.location.href.replace(window.location.origin, '');

          // 看是否有权限
          let hasMatchAuth = true;
          if (
            this.$route &&
            this.$route.meta &&
            this.$route.meta.authKey &&
            Array.isArray(this.$route.meta.authKey)
          ) {
            this.$route.meta.authKey.every(key => {
              // 只要有一个不符合，返回false
              if (!(this.authList || []).includes(key)) {
                hasMatchAuth = false;
                return false;
              }
              return true;
            });
          }
          if (hasMatchAuth) {
            // 有权限
            this.goRedirect(redirctUrl);
          } else {
            // 无权限，全部跳转到welcome
            this.goRedirect('/manage/welcome');
          }
        }
      }
    },

    goRedirect(path) {
      this.$router.replace(path);
    },

    /**
     * 跳转登录页面
     */
    gotoLogin() {
      // 是管理页面才跳转登录，如是其他页面保持路由不变
      if (isManage()) {
        this.$store.commit('clearSession', null);
        this.goRedirect('/login');
      }
    },
  },
};
</script>

<style lang="less" scoped>
.privacy-content-footer {
  .privacy-content-footer-checkbox {
    text-align: left;
    padding: 15px 0;
  }

  .privacy-content-footer-btn {
    text-align: right;
    padding-bottom: 15px;
  }
}
</style>
